import { createContext, type ReactNode, useContext, useMemo, useState } from 'react';
import {
  initialInterfaceLayoutPreferences,
  type InterfaceLayoutPreferences,
} from '@/app/models/InterfaceLayoutPreferences';
import './InterfaceLayout.css';

export const INTERFACE_LAYOUT_STORAGE_KEY = 'easypdv.interface-layout';

type InterfaceLayoutContextValue = {
  preferences: InterfaceLayoutPreferences;
  updatePreferences: (changes: Partial<InterfaceLayoutPreferences>) => void;
};
type InterfaceLayoutProviderProps = { children: ReactNode };

const readPreferences = (): InterfaceLayoutPreferences => {
  try {
    const raw = localStorage.getItem(INTERFACE_LAYOUT_STORAGE_KEY);
    if (!raw) return initialInterfaceLayoutPreferences;
    const saved = JSON.parse(raw) as InterfaceLayoutPreferences;
    const compact = typeof saved.compact === 'boolean' ? saved.compact : false;
    const sidebarMinimized = typeof saved.sidebarMinimized === 'boolean' ? saved.sidebarMinimized : false;
    const fixedHeader = typeof saved.fixedHeader === 'boolean' ? saved.fixedHeader : true;
    const centeredContent = typeof saved.centeredContent === 'boolean' ? saved.centeredContent : false;
    return { compact, sidebarMinimized, fixedHeader, centeredContent };
  } catch {
    return initialInterfaceLayoutPreferences;
  }
};

const InterfaceLayoutContext = createContext<InterfaceLayoutContextValue>({
  preferences: initialInterfaceLayoutPreferences,
  updatePreferences: () => {},
});

export const InterfaceLayoutProvider = ({ children }: InterfaceLayoutProviderProps) => {
  const [preferences, setPreferences] = useState(readPreferences);
  const value = useMemo<InterfaceLayoutContextValue>(
    () => ({
      preferences,
      updatePreferences: (changes) => {
        const nextPreferences = { ...preferences, ...changes };
        try {
          localStorage.setItem(INTERFACE_LAYOUT_STORAGE_KEY, JSON.stringify(nextPreferences));
        } catch {
          /* O layout continua disponível quando o armazenamento está bloqueado. */
        }
        setPreferences(nextPreferences);
      },
    }),
    [preferences],
  );

  return <InterfaceLayoutContext.Provider value={value}>{children}</InterfaceLayoutContext.Provider>;
};

export const useInterfaceLayout = () => useContext(InterfaceLayoutContext);
